import type { Jid } from "../xmpp/jid"
import {
  backfillActiveConversation,
  clearRemovedFrom,
  closeConversation,
  markActiveConversationRead,
  openConversation,
  reseed,
  setConversationMuted,
  startDirectConversation,
} from "./actions/conversations"
import {
  addGroupMembers,
  createGroup,
  demoteGroupAdmin,
  ensureHandles,
  getGroup,
  promoteGroupAdmin,
  removeGroupMembers,
  renameGroup,
  resolveHandles,
  setGroupArchived,
} from "./actions/groups"
import {
  clearRefusal,
  clearNotification,
  editMessage,
  resendMessage,
  retractMessage,
  sendMessage,
  sendShare,
  onBounce,
  onEdit,
  onMessage,
  onRetraction,
} from "./actions/messages"
import { onChatState, sendChatState } from "./actions/typing"
import {
  attachmentUrl,
  type AttachmentUrls,
  uploadAttachment,
} from "./actions/attachments"
import type { AttachmentRef, ChatState, SharedItemRef } from "../types"
import { useVuplexStore } from "../../vuplex/vuplexStore"
import { ChatSession } from "./session"
import { createChatStore } from "./store"

export function createChatRuntime() {
  const store = createChatStore()
  const attachmentUrls: AttachmentUrls = new Map()

  // XEP-0357 push follows the native "Push notifications" toggle. `undefined`
  // means no wrapper (web), which counts as enabled so a web session keeps the
  // per-user row alive for the user's phone (FAME-853). Gated on `online`: the
  // enable/disable IQ is only meaningful once the socket is up, and this runs
  // both when the toggle changes (the store subscription) and on every fresh
  // connect (`onOnline`), since either signal can land first.
  const syncPush = () => {
    if (store.getState().connState !== "online") return
    const enabled = useVuplexStore.getState().pushNotificationsEnabled
    session.client?.setPushEnabled(enabled !== false)
  }

  // Annotated: the callbacks below name `session`, so its type cannot be
  // inferred from its own initialiser.
  const session: ChatSession = new ChatSession({
    onAuthFailed: () => session.reboot("session token rejected"),
    onBounce: (b) => onBounce(session, store, b),
    onConnStateChange: store.getState().setConnState,
    onMessage: (m) => onMessage(session, store, m),
    // Seed the conversation list before the socket opens, and again after
    // every reboot — the list is HTTP, not something stanzas deliver.
    onAuthenticated: (token) => reseed(token, store),
    onOnline: () => {
      session.markLive()
      syncPush()
      backfillActiveConversation(session, store)
    },
    onRetraction: (m) => onRetraction(session, store, m),
    onEdit: (m) => onEdit(session, store, m),
    onChatState: (n) => onChatState(session, store, n),
    reset: store.getState().reset,
    setConfig: store.getState().setConfig,
  })

  const unsubscribePush = useVuplexStore.subscribe(syncPush)

  return {
    store,
    session,
    actions: {
      sendMessage: (peer: Jid, body: string, attachment?: AttachmentRef) =>
        sendMessage(session, store, peer, body, attachment),
      uploadAttachment: (
        peer: Jid,
        file: File,
        onProgress: (fraction: number) => void
      ) => uploadAttachment(session, peer, file, onProgress),
      attachmentUrl: (id: string, opts?: { fresh?: boolean }) =>
        attachmentUrl(session, attachmentUrls, id, opts),
      sendShare: (peer: Jid, ref: SharedItemRef, body: string) =>
        sendShare(session, store, peer, ref, body),
      resendMessage: (peer: Jid, mId: string) =>
        resendMessage(session, store, peer, mId),
      retractMessage: (peer: Jid, mId: string) =>
        retractMessage(session, store, peer, mId),
      editMessage: (peer: Jid, mId: string, body: string) =>
        editMessage(session, store, peer, mId, body),
      sendChatState: (peer: Jid, state: ChatState) =>
        sendChatState(session, store, peer, state),
      openConversation: (peer: Jid) => openConversation(session, store, peer),
      startDirectConversation: (peer: Jid) =>
        startDirectConversation(session, store, peer),
      closeConversation: () => closeConversation(store),
      clearRemovedFrom: () => clearRemovedFrom(store),
      setConversationMuted: (peer: Jid, muted: boolean) =>
        setConversationMuted(session, store, peer, muted),
      markActiveConversationRead: () =>
        markActiveConversationRead(session, store),
      clearRefusal: () => clearRefusal(store),
      clearNotification: (peer: Jid, id: string) =>
        clearNotification(store, peer, id),
      reconnect: () => session.reconnect(),

      createGroup: (name: string, memberIds: string[]) =>
        createGroup(session, store, name, memberIds),
      getGroup: (id: string) => getGroup(session, id),
      renameGroup: (id: string, name: string) =>
        renameGroup(session, store, id, name),
      setGroupArchived: (id: string, archived: boolean) =>
        setGroupArchived(session, store, id, archived),
      addGroupMembers: (id: string, memberIds: string[]) =>
        addGroupMembers(session, store, id, memberIds),
      removeGroupMembers: (id: string, memberIds: string[]) =>
        removeGroupMembers(session, store, id, memberIds),
      promoteGroupAdmin: (id: string, userId: string) =>
        promoteGroupAdmin(session, id, userId),
      demoteGroupAdmin: (id: string, userId: string) =>
        demoteGroupAdmin(session, id, userId),

      ensureHandles: (userIds: string[]) => ensureHandles(session, userIds),
      resolveHandles: (handles: string[]) => resolveHandles(session, handles),
    },
    // Wrapped, not passed: a bare method reference loses its receiver, and
    // these reach the session's #private fields.
    start: () => session.start(),
    stop: () => {
      unsubscribePush()
      session.stop()
    },
  }
}

export type ChatRuntime = ReturnType<typeof createChatRuntime>

// A hot-swapped runtime would strand the live session: the old one keeps the
// socket while the UI reads a fresh, empty store.
if (import.meta.hot) {
  import.meta.hot.accept(() => import.meta.hot?.invalidate())
}
